import React, { useState } from 'react';
import { connect } from 'react-redux';

import { Form, InputGroup } from 'react-bootstrap-5';
import { setFilterAttribute } from 'bundles/connect/components/store/actions/criteria';
import CriteriaDecorator from '../../../helpers/criteria_decorator';
import { formatWithCommas } from 'helpers/formatters/number_formats';
import type { Criteria } from 'bundles/connect/types';

interface RangeInputProps {
  addOn?: React.ReactNode,
  commaSeparated?: boolean,
  criteria: Criteria,
  disabled?: boolean,
  float?: boolean,
  maxAttribute: string,
  minAttribute: string,
  percent?: boolean,
  placeholder: string,
  setFilterAttribute: (attribute: string, value: string, options: {
    float?: boolean,
    int?: boolean,
    percent?: boolean,
    array?: boolean,
    debounce?: boolean
  }) => void,
  type?: 'text' | 'currency' | 'number';
}

function RangeInput({
  commaSeparated = true,
  criteria,
  disabled = false,
  float,
  maxAttribute,
  minAttribute,
  percent,
  placeholder,
  addOn,
  setFilterAttribute,
  type = 'text'
}: RangeInputProps) {
  const decoratedCriteria = new CriteriaDecorator(criteria);
  const initMinValue = decoratedCriteria[minAttribute] ?
    decoratedCriteria[minAttribute]() :
    criteria[minAttribute];
  const initMaxValue = decoratedCriteria[maxAttribute] ?
    decoratedCriteria[maxAttribute]() :
    criteria[maxAttribute];

  const [minValue, setMinValue] = useState(initMinValue && commaSeparated ? formatWithCommas(initMinValue) : initMinValue);
  const [maxValue, setMaxValue] = useState(initMaxValue && commaSeparated ? formatWithCommas(initMaxValue) : initMaxValue);
  const isFloat = float || percent;
  const allowPrependZero = isFloat && (minValue === 0 || maxValue === 0);

  const handleOnChange = (isMin: boolean, value: string) => {
    if (!isFloat && value === '0') return;

    let formattedValue = value;
    const numericValue = value.replace(/[^0-9.]/g, '');

    if (numericValue) {
      switch (type) {
      case 'currency':
        formattedValue = `$${formatWithCommas(numericValue)}`;
        break;
      case 'number':
        formattedValue = commaSeparated ? formatWithCommas(numericValue) : numericValue;
        break;
      default:
        break;
      }
    } else {
      formattedValue = '';
    }

    if (isMin) {
      setMinValue(formattedValue);
    } else {
      setMaxValue(formattedValue);
    }
    setFilterAttribute(isMin ? minAttribute : maxAttribute, value, { int: !percent && !float, float, percent });
  };

  return (
    <div className="d-flex align-items-center">
      <InputGroup>
        {addOn && <InputGroup.Text>{addOn}</InputGroup.Text>}
        <Form.Control
          className={minValue ? 'active' : ''}
          disabled={disabled}
          placeholder={`Min ${placeholder}`}
          step={isFloat ? '0.1' : undefined}
          type={isFloat ? 'number' : 'text'}
          value={minValue}
          onChange={e => handleOnChange(true, e.target.value)}
        />
      </InputGroup>
      <div className="px-1">-</div>
      <InputGroup>
        {addOn && <InputGroup.Text>{addOn}</InputGroup.Text>}
        <Form.Control
          className={maxValue ? 'active' : ''}
          disabled={disabled}
          placeholder={`Max ${placeholder}`}
          step={isFloat ? '0.1' : undefined}
          type={isFloat ? 'number' : 'text'}
          value={allowPrependZero ? maxValue : maxValue || ''}
          onChange={e => handleOnChange(false, e.target.value)}
        />
      </InputGroup>
    </div>
  );
}

const mapStateToProps = ({ criteria }) => ({ criteria });

export default connect(mapStateToProps, { setFilterAttribute })(RangeInput);
